import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { DESTRUCTIVE_OUTLINE } from '../../../components/design-system/admin';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { VendorPlan } from '../our-vendors/models/vendor.model';
import { OfferDraft, OfferEditor, defaultOffer, offerFromApi, offerPayload } from './OfferEditor';

/**
 * This vendor's own prices (docs/pricing-v2.md, Offers). Set at approval,
 * changed here any time: the vendor's plan page and checkout follow at once.
 */
export function VendorOfferCard({ vendorId, plans }: { vendorId: string; plans: VendorPlan[] }) {
  const { t } = useTranslation();
  const plansApi = usePlansApi();
  const [saved, setSaved] = useState<OfferDraft | null>(null);
  const [draft, setDraft] = useState<OfferDraft | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.resolve(plansApi.getVendorOffer(vendorId))
      .then((res: any) => {
        const o = offerFromApi(res?.offer);
        setSaved(o);
        setDraft(o);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const run = async (offer: any | null) => {
    setBusy(true);
    try {
      const res: any = await plansApi.saveVendorOffer(vendorId, offer);
      const o = offerFromApi(res?.offer);
      setSaved(o);
      setDraft(o);
      toast.success(offer ? t('PRICING.OFFER.SAVED') : t('PRICING.OFFER.REMOVED'));
    } catch {
      toast.error(t('PRICING.OFFER.ERROR'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title={t('PRICING.OFFER.TITLE')} description={t('PRICING.OFFER.DESC')} testId="vendor-offer-card">
      {draft ? (
        <div className="flex flex-col gap-4">
          <OfferEditor plans={plans} value={draft} onChange={setDraft} />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <Button variant="outline" size="sm" className={DESTRUCTIVE_OUTLINE} disabled={busy || !saved} onClick={() => run(null)}>
              {t('PRICING.OFFER.REMOVE_OFFER')}
            </Button>
            <Button variant="outline" disabled={busy || !dirty || !offerPayload(draft)} onClick={() => run(offerPayload(draft))} data-testid="offer-save">
              {t('PRICING.OFFER.SAVE')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t('PRICING.OFFER.NONE')}</p>
          <Button variant="outline" size="sm" onClick={() => setDraft(defaultOffer(plans))} data-testid="offer-create">
            {t('PRICING.OFFER.CREATE')}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
